<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="clients-page-title">
      {{ $t('administration.features.title') }}
    </h4>

    <div class="card mt-4">
      <table class="table table-responsive-md">
        <thead>
        <tr>
          <th scope="col">{{ $t('administration.features.name_label') }}</th>
          <th scope="col">{{ $t('administration.features.price_label') }}</th>
          <th scope="col">{{ $t('administration.features.price_yearly_label') }}</th>
          <th scope="col">{{ $t('administration.features.stripe_id_label') }}</th>
          <th width="100"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="object in tableItems">
          <td>
            <span>
              {{object.name}}
            </span>
          </td>
          <td>
            <span>
              {{ object.price }}
            </span>
          </td>
          <td>
            <span>
              {{ object.price_yearly }}
            </span>
          </td>
          <td>
            <span>
              {{ object.stripe_id }}
            </span>
          </td>
          <td>
            <span class="text-muted mr-3 cursor-pointer" qid="feature-edit-button" @click="showEditFeatureModal(object)">
              <font-awesome-icon icon="pen"/>
            </span>
          </td>
        </tr>
        <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
          <td colspan="10">
            <h5 class="mt-3">
              {{ $t('system.no_results') }}
            </h5>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="mt-3 pagination row">
      <div class="col-md-6">
        <div class="row">
          <div class="float-left">
            <span class="text-muted pagination-counter">
              {{ $t('pagination.entries_per_page') }}:
            </span>
          </div>
          <div class="float-left">
            <multiselect
                v-model="perPage"
                :options="perPageOptions"
                track-by="value"
                label="value"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="create-org-form-organization-level"
                @select="perPageChanged"
                :placeholder="selectedPerPage"
                @remove="perPageRemoved"
            ></multiselect>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="selectedPerPage"
            aria-controls="my-table"
            @change="pageChanged"
            limit="1"
        >
          <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
          <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
          <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
          <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
          <div slot="ellipsis-text">
            <b-spinner small type="grow"></b-spinner>
            <b-spinner small type="grow"></b-spinner>
            <b-spinner small type="grow"></b-spinner>
          </div>
          <span class="text-muted" slot="page" slot-scope="{ page, active }">
            <span v-if="active">{{getPerPage}}</span>
          </span>
        </b-pagination>
      </div>
    </div>


    <!-- Edit client modal template -->
    <b-modal ref="edit-feature" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-client-title">
          {{ $t('administration.features.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-c-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editFeatureFormSubmit" qid="edit-feature-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-guide-name-label">
              {{ $t('administration.features.price_label') }}
            </label>
            <b-form-input
                qid="create-guide-form-name"
                type="number"
                v-model="featureForm.price"
                required
                :placeholder="$t('administration.features.price_label')"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditFeatureModal"
                    qid="edit-feature-cancel-button"
          >
            {{ $t('administration.clients.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-feature-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('administration.clients.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import permissionCheck from '@/util/ACL/permissionCheck.js'

  export default {
    name: 'Guide',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('administration.title'),
            to: '/administration'
          },
          {
            text: this.$t('administration.features.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        featureForm: {
          id: '',
          price: ''
        },
        error: '',
        loading: true,
        buttonDisabled: false,
        selectedContentType: [],
        selectedType: [],
        selectedLanguage: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    methods: {
      loadFeatures(page, perPage) {
        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        piincHttp.get('features', { params: {
            page: pageNumber,
            per_page: perPageNumber
          }}).then(function(response) {
          self.tableItems = response.data.items;
          self.loading = false;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {

        });
      },
      pageChanged(page) {
        this.loadFeatures(page, '', this.currentTab)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadFeatures(1, perPage.value, this.currentTab)
        self.selectedPerPage = perPage.value
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadFeatures(1, self.selectedPerPage)
      },
      isGranted(permission) {
        return permissionCheck(permission)
      },
      editFeatureFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        piincHttp.put('features/' + this.featureForm.id, this.featureForm).then(function(response) {
          self.hideEditFeatureModal();
          self.loadFeatures(self.currentPage);
          self.$toastr('success', self.$t('administration.features.update.success'))
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showEditFeatureModal(object) {
        this.featureForm.id = object.id
        this.featureForm.price = object.price

        this.$refs['edit-feature'].show()
      },
      hideEditFeatureModal() {
        this.error = ''
        this.$refs['edit-feature'].hide()
      }

    },
    components: {

    },
    created () {
      this.loadFeatures();
    }
  }
</script>
